import { useState, useCallback, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { pollService } from "../services/PollService";
import { handleError, ErrorFactory, logError } from "../lib/error-handling";
import { logger } from "@core/lib/logger";

// Types pour les polls
export interface PollData {
  type: "date" | "form" | "quizz" | "availability";
  title: string;
  description?: string;
  slug?: string;
  selectedDates?: string[];
  timeSlotsByDate?: Record<string, any>;
  dateGroups?: any[];
  questions?: any[];
  settings?: Record<string, any>;
  parsedAvailabilities?: any[];
  proposedSlots?: any[];
  participantEmails?: string[];
  invitedEmails?: string[]; // NOUVEAU: Emails invités pour les rappels
}

export interface DatePollData extends PollData {
  type: "date";
  selectedDates: string[];
  timeSlotsByDate?: Record<string, any>;
  dateGroups?: any[];
  settings?: {
    timeGranularity?: number;
    allowAnonymousVotes?: boolean;
    allowMaybeVotes?: boolean;
    sendNotifications?: boolean;
    expiresAt?: string; // Date de fin du sondage
    selectedDates?: string[];
  };
}

export interface FormPollData extends PollData {
  type: "form";
  questions: any[];
}

export const usePolls = () => {
  const { user, profile } = useAuth();
  const [polls, setPolls] = useState<PollData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPolls = useCallback(async () => {
    console.log("🔄 [USE_POLLS] fetchPolls called");
    console.log("🔍 [USE_POLLS] fetchPolls - user:", user);
    console.log("🔍 [USE_POLLS] fetchPolls - profile:", profile);
    setLoading(true);
    setError(null);

    try {
      if (!user) {
        console.log("👤 [USE_POLLS] No user - using guest mode");
        console.log("👀 [USE_POLLS] Guest mode - no polls will be fetched");
        setPolls([]);
        return;
      }

      console.log("📋 [USE_POLLS] Calling pollService.getPollsByUser", {
        userId: user?.id,
      });
      const userPolls = await pollService.getPollsByUser(user.id);
      console.log("✅ [USE_POLLS] Polls fetched successfully", {
        count: userPolls?.length || 0,
        types: userPolls?.map((p) => p.type) || [],
      });
      setPolls(userPolls);
    } catch (error) {
      console.error("❌ [USE_POLLS] Error fetching polls", error);
      handleError(error);
      setError("Erreur lors de la récupération des sondages");
    } finally {
      console.log(
        "🏁 [USE_POLLS] fetchPolls completed, setting loading to false",
      );
      setLoading(false);
    }
    // Bug #8 — dépendre de `user?.id` (primitif) plutôt que de `user` (objet),
    // car onAuthStateChange émet INITIAL_SESSION puis SIGNED_IN avec un nouvel
    // objet user (mais même id), ce qui déclenchait deux fetches en cascade.
  }, [user?.id]);

  // useEffect pour appeler fetchPolls au montage et quand l'utilisateur change
  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  const generateSlug = useCallback((title: string): string => {
    // Bug hunt 2026-04-23 #8 : retirer les balises HTML avant slugification
    // pour éviter de conserver les noms de tags dans le slug
    // (`<script>alert(1)</script>` → `alert1` plutôt que `scriptalert1script`).
    // On tronque aussi plus agressivement (40 au lieu de 50 chars) pour que
    // le résidu d'un payload éventuel soit coupé.
    const stripped =
      typeof document !== "undefined"
        ? new DOMParser().parseFromString(title, "text/html").body
            .textContent || ""
        : title.replace(/<[^>]*>/g, "");
    return (
      stripped
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
        .replace(/[^a-z0-9\s-]/g, "") // Garder seulement lettres, chiffres, espaces et tirets
        .trim()
        .replace(/\s+/g, "-") // Remplacer espaces par tirets
        .replace(/-+/g, "-") // Éviter les tirets multiples
        .replace(/^-+|-+$/g, "") // Pas de tiret en début/fin
        .substring(0, 40) + // Limiter la longueur
      "-" +
      uuidv4().substring(0, 8)
    ); // Ajouter un ID unique
  }, []);

  const createPoll = useCallback(
    async (pollData: PollData): Promise<{ poll?: any; error?: string }> => {
      setLoading(true);
      setError(null);

      try {
        // 📜 DOCTRINE PRICING (11 mai 2026) — packages/core/src/constants/quotas.ts
        //    Création toujours gratuite et illimitée pour ANONYMOUS et SOLO.
        //    FREE : plafond de 10 sondages actifs (MAX_ACTIVE_POLLS.FREE).
        //    Au-delà, l'utilisateur doit archiver/supprimer un sondage ou
        //    passer à Solo.
        if (user?.id && profile) {
          // Skip cap if profile not yet loaded — éviter faux positif FREE
          // quand utilisateur SOLO mais profile encore en cours de fetch.
          const { determineUserPlan, getMaxActivePolls, isPaidPlanType } =
            await import("@core/constants/quotas");
          const userPlan = determineUserPlan(
            true,
            isPaidPlanType(profile?.plan_type),
          );
          const maxActive = getMaxActivePolls(userPlan);
          if (maxActive !== null) {
            const activeCount = await pollService.countActivePollsByUser(
              user.id,
            );
            if (activeCount >= maxActive) {
              logger.info(
                "Poll creation blocked: FREE plan cap reached",
                "poll",
                {
                  userId: user.id,
                  activeCount,
                  maxActive,
                },
              );
              return {
                error:
                  "POLL_LIMIT_REACHED:Vous avez atteint la limite de 10 sondages actifs. Archivez un sondage existant ou passez à Solo pour une création illimitée.",
              };
            }
          }
        }

        // Validation selon le type
        if (pollData.type === "date") {
          if (
            !Array.isArray(pollData.selectedDates) ||
            pollData.selectedDates.length === 0
          ) {
            throw ErrorFactory.validation(
              "No dates selected for poll creation",
              "Sélectionnez au moins une date pour créer le sondage.",
            );
          }
        } else if (pollData.type === "form") {
          if (
            !Array.isArray(pollData.questions) ||
            pollData.questions.length === 0
          ) {
            throw ErrorFactory.validation(
              "No questions in form",
              "Ajoutez au moins une question pour créer le formulaire.",
            );
          }
        }

        // Utiliser le slug fourni si disponible, sinon générer un nouveau slug.
        // `slugWasProvided` distingue deux cas de collision (cf. catch plus bas) :
        // un slug FOURNI provient d'un brouillon déjà connu de l'UI / d'un lien
        // partagé → il ne doit JAMAIS être remplacé en silence.
        const slugWasProvided = !!pollData.slug;
        const slug = pollData.slug || generateSlug(pollData.title);

        // Identifier le créateur : user ID si connecté, deviceId + fingerprint si invité
        let creatorId: string | null = user?.id || null;
        const creatorType: "user" | "guest" = user?.id ? "user" : "guest";
        let guestFingerprint: string | null = null;

        if (!user?.id) {
          const { enrichPollForGuest } =
            await import("@core/lib/guestIdentityService");
          const guestIds = await enrichPollForGuest({});
          creatorId = guestIds.creator_id;
          guestFingerprint = guestIds.guest_fingerprint;
        }

        // Préparer les données de base pour PollService
        const basePollData = {
          type: pollData.type,
          title: pollData.title,
          description: pollData.description,
          slug,
          status: "active" as const,
          // Données spécifiques selon le type
          ...(pollData.type === "date" && {
            selectedDates: pollData.selectedDates,
            timeSlotsByDate: pollData.timeSlotsByDate,
            dateGroups: pollData.dateGroups,
            showTimeSlots: pollData.settings?.showTimeSlots,
            notificationsEnabled: pollData.settings?.notificationsEnabled,
            participantEmails: pollData.settings?.participantEmails,
            invitedEmails: pollData.invitedEmails, // NOUVEAU: Stocker les emails invités
            settings: pollData.settings, // Ajouter tous les settings y compris expiresAt
          }),
          ...(pollData.type === "form" && {
            questions: pollData.questions,
            settings: pollData.settings,
            displayMode: (pollData as any).displayMode,
            themeId: (pollData as any).themeId,
            resultsVisibility: (pollData as any).resultsVisibility,
            conditionalRules: (pollData as any).conditionalRules,
          }),
          ...(pollData.type === "quizz" && {
            questions: pollData.questions,
            settings: pollData.settings,
          }),
          ...(pollData.type === "availability" && {
            parsedAvailabilities: pollData.parsedAvailabilities,
            proposedSlots: pollData.proposedSlots,
            settings: pollData.settings,
          }),
        };

        // Enrichir les données guest avant insertion Supabase
        let pollDataForService;
        if (!user?.id) {
          const { enrichPollForGuest } =
            await import("@core/lib/guestIdentityService");
          const guestIds = await enrichPollForGuest({
            creator_id: creatorId,
            guest_fingerprint: guestFingerprint,
          });

          pollDataForService = {
            ...basePollData,
            creator_id: guestIds.creator_id,
            creator_type: "guest",
            guest_fingerprint: guestIds.guest_fingerprint,
          };

          logger.debug("Enriched poll data for guest user", "poll", {
            creator_id: guestIds.creator_id,
            hasGuestFingerprint: !!guestIds.guest_fingerprint,
          });
        } else {
          pollDataForService = {
            ...basePollData,
            creator_id: creatorId,
            creator_type: creatorType,
            guest_fingerprint: guestFingerprint,
          };
        }

        // Créer le poll via PollService (retry 1x sur collision slug)
        let createdPoll;
        try {
          createdPoll = await pollService.createPoll(pollDataForService);
        } catch (e) {
          const code = (e as { code?: string } | null)?.code;
          if (code === "SLUG_COLLISION") {
            // Deux scénarios de collision très différents :
            //
            // 1. Slug FOURNI (brouillon re-persisté, ex. restore localStorage
            //    cross-mount). Le slug existe déjà en DB et l'UI / un lien de
            //    vote partagé le connaît déjà. Régénérer un nouveau slug ici
            //    crée une ligne sous un slug que PERSONNE n'a partagé → le lien
            //    en main pointe sur un slug inexistant = lien de vote mort
            //    (incident `retournement-ccd2b50e`, 2026-06-13). On réutilise
            //    donc la ligne existante (slug préservé), à condition qu'elle
            //    nous appartienne.
            // 2. Slug AUTO-GÉNÉRÉ (création fraîche, aucun lien encore partagé).
            //    Collision aléatoire ~impossible mais possible → régénérer est
            //    sans danger car rien ne référence l'ancien slug.
            if (slugWasProvided) {
              const existing = await pollService.getPollBySlug(
                pollDataForService.slug,
              );
              // Vérif de propriété. `getPollBySlug` transmet l'empreinte de
              // l'appelant au RPC `get_poll_by_slug`, qui ne RÉVÈLE
              // `creator_id` / `guest_fingerprint` (non-null) qu'au vrai
              // créateur (sécurité anti-IDOR, commit e2ca7734). Donc un champ
              // d'identité non-null ET égal au nôtre = preuve de propriété,
              // côté serveur.
              //  - connecté : match sur `creator_id` ;
              //  - INVITÉ (la masse des utilisateurs) : match sur
              //    `guest_fingerprint` (empreinte navigateur stable).
              const myCreatorId = pollDataForService.creator_id ?? null;
              const myFingerprint =
                (pollDataForService as { guest_fingerprint?: string | null })
                  .guest_fingerprint ?? null;
              const ownedByUser =
                !!existing &&
                existing.creator_id != null &&
                existing.creator_id === myCreatorId;
              const ownedByGuest =
                !!existing &&
                myFingerprint != null &&
                (existing as { guest_fingerprint?: string | null })
                  .guest_fingerprint === myFingerprint;
              const owned = ownedByUser || ownedByGuest;
              if (owned) {
                logger.info(
                  "Slug collision = re-persist du même brouillon, réutilisation (slug préservé)",
                  "poll",
                  { slug: existing!.slug },
                );
                createdPoll = existing!;
              } else {
                // Collision avec un sondage qu'on ne peut pas revendiquer
                // (tiers, ou propriété non vérifiable). Ne pas réutiliser pour
                // éviter toute fuite ; régénérer un slug neuf.
                logger.warn(
                  "Slug collision non revendicable, retry avec slug régénéré",
                  "poll",
                );
                pollDataForService = {
                  ...pollDataForService,
                  slug: generateSlug(pollData.title),
                };
                createdPoll = await pollService.createPoll(pollDataForService);
              }
            } else {
              logger.warn(
                "Slug collision, retry with regenerated slug",
                "poll",
              );
              pollDataForService = {
                ...pollDataForService,
                slug: generateSlug(pollData.title),
              };
              createdPoll = await pollService.createPoll(pollDataForService);
            }
          } else {
            throw e;
          }
        }

        logger.info("✅ Poll créé via PollService", "poll", {
          pollId: createdPoll.id,
          type: createdPoll.type,
          slug: createdPoll.slug,
        });

        // Émettre un événement pour notifier les composants de la création
        window.dispatchEvent(
          new CustomEvent("pollCreated", {
            detail: { poll: createdPoll },
          }),
        );

        return { poll: createdPoll };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Erreur inconnue";
        logger.error("❌ Erreur création poll", "poll", { error });
        return { error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    // Bug #8 — clé primitive pour stabiliser la référence du callback
    [user?.id, generateSlug],
  );

  // Mise à jour d'un sondage existant. Utilisé pour éviter de créer un
  // doublon quand l'utilisateur ré-enregistre un sondage déjà créé
  // (édition via ?edit=<id> OU re-clic "créer" après une première création
  // dans la même session). La RLS Supabase + le check d'ownership dans
  // pollService.updatePoll restent les gardiens d'autorisation.
  const updatePoll = useCallback(
    async (
      id: string,
      updates: Record<string, unknown>,
    ): Promise<{ poll?: any; error?: string }> => {
      setLoading(true);
      setError(null);
      try {
        const updated = await pollService.updatePoll(id, updates as any);
        logger.info("✅ Poll mis à jour via PollService", "poll", {
          pollId: id,
        });
        window.dispatchEvent(
          new CustomEvent("pollUpdated", { detail: { poll: updated } }),
        );
        return { poll: updated };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Erreur inconnue";
        logger.error("❌ Erreur mise à jour poll", "poll", { error });
        return { error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getUserPolls = useCallback(async () => {
    if (!user) return [];

    try {
      const userPolls = await pollService.getPollsByUser(user.id);
      return userPolls;
    } catch (error) {
      handleError(error);
      return [];
    }
    // Bug #8 — idem
  }, [user?.id]);

  return {
    polls,
    loading,
    error,
    createPoll,
    updatePoll,
    getUserPolls,
    generateSlug,
  };
};
